/**
 * Tooltip view models for abilities (player and enemy), live casts and auras. Pure: no DOM.
 *
 *   describeAbility(ability, caster, content, opts) -> TooltipModel
 *   describeAura(instance, def, ctx)                -> TooltipModel
 *   describeUnitCast(unit, world, content)          -> TooltipModel | null
 */
import { BASE_GCD_MS, MIN_GCD_MS } from '@keystone/sim';
import type {
  AbilityDef,
  AbilityTag,
  AuraDef,
  Condition,
  EffectSpec,
  ProcSpec,
  AuraInstance,
  ClassDef,
  DeepReadonly,
  EffectiveStats,
  EnemyAbility,
  EnemyAbilityKind,
  EnemyTargetRule,
  Stats,
  Unit,
  World,
} from '@keystone/sim';
import { CLASSES } from '@keystone/content';
import { castLabel, diffAbility, passiveLines } from './diff';
import { amountText, auraClauses, condText, DEFAULT_POWER, everyText, phrases, procSentences, renderEffects, type RenderCtx } from './effects';
import { capitalize, fmtDuration, fmtInt, fmtPct, fmtSec, joinAnd, prettyId, sentence, trimNum } from './format';
import { defaultIconResolver, type IconResolver } from './icon';
import type { TooltipCaster, TooltipContent, TooltipLine, TooltipModel } from './types';

export interface DescribeOptions {
  /** Key shown as "Keybind: 2". */
  keybind?: string | null;
  /** The (compiled) class that owns the ability: resource names + talent passives. */
  classDef?: Pick<ClassDef, 'id' | 'resources' | 'passives'> | null;
  /**
   * Baseline for the talent diff. Default: the same id in the base content classes.
   * Pass null to skip the diff.
   */
  baseAbility?: AbilityDef | null;
  /** Prefix of modification lines (default "Talent"). */
  modLabel?: string;
  /** Icon URL resolver. Default: hud/icons.ts if present. null disables icons. */
  iconUrl?: IconResolver | null;
  /** Force enemy wording (default: auto-detected from EnemyAbility fields). */
  enemy?: boolean;
  /** Live interruptibility of a cast in progress (overrides the def). */
  interruptible?: boolean;
  /** Red lines (e.g. "Not enough Rage"). */
  errors?: readonly string[];
  /**
   * Transforms (rotation rework F2). `from`: this ability is the current form of a bound button
   * ("Replaces Lightning Bolt while Tempest is active"). `into`: forms this ability can turn into.
   */
  transform?: { from?: { name: string; via: string } | null; into?: readonly { name: string; via: string }[] };
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const BASE_ABILITIES = new Map<string, AbilityDef>(CLASSES.flatMap((c) => c.abilities.map((a) => [a.id, a] as const)));
const BASE_CLASS_OF = new Map<string, ClassDef>(CLASSES.flatMap((c) => c.abilities.map((a) => [a.id, c] as const)));
const RESOURCE_NAMES = new Map<string, string>(CLASSES.flatMap((c) => c.resources.map((r) => [r.id, r.name] as const)));

/** The unmodified content definition of a player ability. */
export function baseAbility(id: string): AbilityDef | undefined {
  return BASE_ABILITIES.get(id);
}

const TAG_LABEL: Record<AbilityTag, string> = {
  interrupt: 'Interrupt',
  dispel: 'Dispel',
  defensive: 'Defensive',
  majorDefensive: 'Major Defensive',
  externalDefensive: 'External',
  taunt: 'Taunt',
  aoe: 'AoE',
  builder: 'Builder',
  spender: 'Spender',
  heal: 'Heal',
  groupHeal: 'Group Heal',
  movement: 'Movement',
  cooldown: 'Cooldown',
  execute: 'Execute',
};

const KIND_LABEL: Record<EnemyAbilityKind, string> = {
  bolt: 'Bolt',
  tankbuster: 'Tank Buster',
  frontal: 'Frontal',
  groundAoE: 'Ground AoE',
  pulse: 'Pulse',
  cc: 'Crowd Control',
  heal: 'Heal',
  shield: 'Shield',
  summon: 'Summon',
  charge: 'Charge',
  buff: 'Buff',
};

const TARGET_LABEL: Record<EnemyTargetRule, string> = {
  victim: 'its current target',
  tank: 'the tank',
  random: 'a random player',
  randomNotTank: 'a random non-tank player',
  farthest: 'the farthest player',
  nearest: 'the nearest player',
  healer: 'the healer',
  self: 'itself',
  lowestHpAlly: 'its most injured ally',
  allies: 'its allies',
  none: 'no target',
};

export function isEnemyAbility(a: AbilityDef): a is EnemyAbility {
  return 'kind' in a && 'timer' in a && 'target' in a;
}

// ---------------------------------------------------------------------------
// Casters
// ---------------------------------------------------------------------------

/** Caster numbers from plain stats (compiled character stats, or a unit's effective stats). */
export function casterFromStats(s: Pick<Stats, 'power' | 'haste'> & Partial<Stats>, extra: Partial<TooltipCaster> = {}): TooltipCaster {
  return { power: s.power, haste: s.haste, crit: s.crit, versatility: s.versatility, ...extra };
}

/** Caster numbers for a live unit; pass effective stats (SimQueries.effectiveStats) when available. */
export function casterFromUnit(u: DeepReadonly<Unit>, eff?: EffectiveStats | null): TooltipCaster {
  const s = eff ?? u.stats;
  return {
    power: s.power,
    haste: s.haste,
    crit: s.crit,
    versatility: s.versatility,
    maxHp: u.maxHp,
    damageMult: u.scaling?.damage ?? 1,
  };
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

/** Visit every effect in a tree (conditionals, telegraphs, zones, inline auras). */
function walkEffects(list: readonly EffectSpec[] | undefined, fn: (e: EffectSpec) => void, auraFn?: (a: AuraDef) => void, seen = new Set<object>()): void {
  for (const e of list ?? []) {
    fn(e);
    if (e.type === 'conditional') {
      walkEffects(e.then, fn, auraFn, seen);
      walkEffects(e.else, fn, auraFn, seen);
    } else if (e.type === 'telegraph') walkEffects(e.effects, fn, auraFn, seen);
    else if (e.type === 'zone') {
      walkEffects(e.tick?.effects, fn, auraFn, seen);
      if (e.insideAura) walkAura(e.insideAura, fn, auraFn, seen);
    } else if (e.type === 'applyAura') walkAura(e.aura, fn, auraFn, seen);
  }
}
function walkAura(a: AuraDef, fn: (e: EffectSpec) => void, auraFn: ((a: AuraDef) => void) | undefined, seen: Set<object>): void {
  if (seen.has(a)) return;
  seen.add(a);
  auraFn?.(a);
  walkEffects(a.periodic?.effects, fn, auraFn, seen);
  walkEffects(a.onApply, fn, auraFn, seen);
  walkEffects(a.onRemove, fn, auraFn, seen);
  for (const p of a.procs ?? []) walkEffects(p.effects, fn, auraFn, seen);
}
const abilityTrees = (a: AbilityDef): EffectSpec[] => [...(a.channelStartEffects ?? []), ...a.effects, ...(a.channelEndEffects ?? [])];

type ClassLike = Partial<Pick<ClassDef, 'abilities'>> & Pick<ClassDef, 'passives'>;
const AURA_INDEX = new WeakMap<object, Map<string, AuraDef>>();
/** Every inline aura a class defines (talent auras are not in the content registry). */
function classAuras(cls: ClassLike | undefined): Map<string, AuraDef> | undefined {
  if (!cls) return undefined;
  let m = AURA_INDEX.get(cls);
  if (m) return m;
  const map = new Map<string, AuraDef>();
  const add = (a: AuraDef): void => {
    if (!map.has(a.id)) map.set(a.id, a);
  };
  for (const a of cls.abilities ?? []) walkEffects(abilityTrees(a), () => {}, add);
  for (const p of cls.passives ?? []) walkAura(p, () => {}, add, new Set());
  AURA_INDEX.set(cls, map);
  return map;
}

function makeCtx(ability: AbilityDef, caster: TooltipCaster | null, content: TooltipContent, enemy: boolean, classDef?: DescribeOptions['classDef']): RenderCtx {
  const auras = classAuras((classDef as ClassLike | null | undefined) ?? BASE_CLASS_OF.get(ability.id));
  return {
    caster,
    content,
    enemy,
    targeting: ability.targeting,
    abilityName: ability.name,
    abilityId: ability.id,
    gains: (ability.gain ?? []).map((r) => r.resource),
    formulas: [],
    resourceName: (id) => classDef?.resources.find((r) => r.id === id)?.name ?? RESOURCE_NAMES.get(id) ?? prettyId(id),
    ...(auras ? { auraLookup: (id: string) => auras.get(id) } : {}),
    ...((classDef as ClassLike | null | undefined)?.abilities ? { abilityLookup: (id: string) => (classDef as ClassLike).abilities!.find((a) => a.id === id) } : {}),
  };
}

/**
 * Render context for describing `ability` (or class-level text when null) of a possibly compiled
 * class with no live caster (talent screen, gear cards): default power, the class's own auras and
 * talent-added abilities resolvable by id.
 */
export function classRenderCtx(ability: AbilityDef | null, content: TooltipContent, classDef: ClassDef): RenderCtx {
  if (ability) return makeCtx(ability, null, content, false, classDef);
  // Proc payloads with no `to` hit the event's target (the healed ally, the enemy hit): "the target".
  const ctx = makeCtx({ id: '', name: '', school: 'physical', targeting: 'enemy', effects: [] } as unknown as AbilityDef, null, content, false, classDef);
  delete ctx.abilityName;
  delete ctx.abilityId;
  return ctx;
}

/** `ui.desc` template: `{damage}`, `{damage2}`, `{heal}`, `{absorb}` become live amounts (walk order). */
function fillTemplate(t: string, ability: AbilityDef, ctx: RenderCtx): string {
  const found: Record<'damage' | 'heal' | 'absorb', string[]> = { damage: [], heal: [], absorb: [] };
  const quiet: RenderCtx = { ...ctx, formulas: [] };
  walkEffects(abilityTrees(ability), (e) => {
    if (e.type === 'damage' || e.type === 'heal' || e.type === 'absorb') found[e.type].push(amountText(e.amount, quiet, e.type, e.type).text);
  });
  // Collect the Shift-view formulas once.
  renderEffects(ability.effects, ctx);
  return t.replace(/\{(damage|heal|absorb)(\d*)\}/g, (m, k: 'damage' | 'heal' | 'absorb', n: string) => found[k][(n ? Number(n) : 1) - 1] ?? m);
}

/** Proc triggers that read naturally from the triggering ability's own tooltip. */
const OWN_PROC_ON = new Set<ProcSpec['on']>(['cast', 'castResolved', 'damageDealt', 'critDealt', 'healDealt', 'critHealed', 'interruptDealt']);

/** The aura that turns some button into this ability (transform form), if any. */
function transformSource(ability: AbilityDef, content: TooltipContent): { base: string; aura: AuraDef } | null {
  for (const def of content.auras?.values() ?? []) for (const o of def.overrides ?? []) if (o.with === ability.id) return { base: o.abilityId, aura: def };
  return null;
}
const isTransformGate = (c: Condition | undefined, src: { aura: AuraDef } | null): boolean => !!src && c?.type === 'selfHasAura' && c.auraId === src.aura.id;
// The aura usually shares the form's name ("Becomes Tempest while Tempest is active"): kept short on purpose.
const viaText = (via: string, _form: string): string => via;

const resList = (list: readonly { resource: string; amount: number }[], ctx: RenderCtx): string => list.map((r) => `${fmtInt(r.amount)} ${ctx.resourceName(r.resource)}`).join(', ');

export function rangeLabel(a: AbilityDef): string | null {
  if (a.targeting === 'none' || a.targeting === 'self' || a.targeting === 'cone') return null;
  if (a.melee || (a.targeting === 'enemy' && !a.range)) return 'Melee range';
  if (a.range && a.range > 0) return `${trimNum(a.range, 1)} yd range`;
  return null;
}

function autoTransform(ability: AbilityDef, content: TooltipContent, src: { base: string; aura: AuraDef } | null): DescribeOptions['transform'] {
  if (!content.auras) return undefined;
  const from = src ? { name: content.ability(src.base)?.name ?? prettyId(src.base), via: src.aura.name } : null;
  const into: { name: string; via: string }[] = [];
  for (const def of content.auras.values()) {
    for (const o of def.overrides ?? []) {
      if (o.abilityId !== ability.id) continue;
      const name = content.ability(o.with)?.name ?? prettyId(o.with);
      if (!into.some((x) => x.name === name)) into.push({ name, via: def.name });
    }
  }
  return { from, into };
}

export function describeAbility(ability: AbilityDef, caster: TooltipCaster | null, content: TooltipContent, opts: DescribeOptions = {}): TooltipModel {
  const enemyDef = isEnemyAbility(ability) ? ability : null;
  const enemy = opts.enemy ?? !!enemyDef;
  const ctx = makeCtx(ability, caster, content, enemy, opts.classDef);
  const haste = caster?.haste ?? 0;
  const lines: TooltipLine[] = [];
  const extended: TooltipLine[] = [];

  // ---- header -----------------------------------------------------------------
  const cost = ability.cost?.length ? resList(ability.cost, ctx) : null;
  const gainText = ability.gain?.length ? `Generates ${resList(ability.gain, ctx)}` : null;
  const left1 = cost ?? gainText;
  const range = rangeLabel(ability);
  const cast = castLabel(ability.cast, haste);
  const cd = ability.cooldown;
  const charges = cd && (cd.charges ?? 1) > 1 ? `${cd.charges} charges` : null;
  const cdMs = cd ? (cd.hasteScaled ? cd.durationMs / (1 + haste) : cd.durationMs) : 0;
  const cooldown = cd && cdMs > 0 ? `${fmtDuration(cdMs)} ${charges ? 'recharge' : 'cooldown'}` : null;
  const grid: [string, string][] = [];
  if (left1 || range) grid.push([left1 ?? '', range ?? '']);
  grid.push([cast, cooldown ?? '']);
  if (charges) grid.push([charges, '']);
  if (cost && gainText) lines.push({ text: gainText, tone: 'white' });

  // ---- requirements -----------------------------------------------------------
  if (!enemy && ability.gcdMs === 0) lines.push({ text: 'Off the global cooldown', tone: 'white' });
  if (ability.requiresFacing) lines.push({ text: 'Requires facing target', tone: 'white' });
  if (ability.canCastWhileMoving) lines.push({ text: 'Usable while moving', tone: 'white' });
  if (ability.usableWhileCasting) lines.push({ text: 'Usable while casting', tone: 'white' });
  const src = enemy ? null : transformSource(ability, content);
  if (ability.usableIf && !isTransformGate(ability.usableIf, src)) {
    const c = condText(ability.usableIf, ctx);
    if (c) lines.push({ text: sentence(`usable only if ${c}`), tone: 'white' });
  }

  // ---- enemy info -------------------------------------------------------------
  if (enemy && ability.cast && ability.cast.type !== 'instant') {
    const intr = opts.interruptible ?? !!ability.interruptible;
    lines.push(intr ? { text: 'Interruptible', tone: 'good' } : { text: 'Cannot be interrupted', tone: 'error' });
  }
  if (enemyDef) {
    if (enemyDef.target !== 'none' && enemyDef.target !== 'self') lines.push({ text: `Targets ${TARGET_LABEL[enemyDef.target] ?? enemyDef.target}`, tone: 'white' });
    if (enemyDef.dispellable) lines.push({ text: `${capitalize(enemyDef.dispellable)} effect: can be dispelled`, tone: 'good' });
  }

  // ---- description (live numbers) ---------------------------------------------
  const desc: string[] = [];
  const override = !enemy ? ability.ui?.desc : undefined;
  if (override) desc.push(fillTemplate(override, ability, ctx));
  else {
    desc.push(...renderEffects(ability.channelStartEffects ?? [], ctx));
    if (ability.cast?.type === 'channel') {
      const dur = ability.cast.durationMs / (1 + haste);
      const ticks = Math.max(1, ability.cast.ticks);
      const body = phrases(ability.effects, ctx);
      if (body) {
        if (ticks === 1) desc.push(sentence(`at the end of the ${fmtDuration(dur)} channel, ${enemy ? 'the caster ' : ''}${body}`));
        else if (ability.effects.length === 1 && ability.effects[0]!.type !== 'conditional') desc.push(sentence(`${body} ${everyText(dur / ticks)} for ${fmtDuration(dur)}`));
        else desc.push(sentence(`channels for ${fmtDuration(dur)}`), sentence(`${everyText(dur / ticks)}, it ${body}`));
      }
      // +rotation rework (F10): channel end effects.
      const end = ability.channelEndEffects?.length ? phrases(ability.channelEndEffects, ctx) : '';
      if (end) desc.push(sentence(`${ability.channelEndOn === 'any' ? 'when the channel ends, even early' : 'if the full channel completes'}, ${end}`));
    } else desc.push(...renderEffects(ability.effects, ctx));
  }
  // Class passives keyed on this ability (hidden proc engines): "Has a 30% chance to grant you ...".
  const ownCls = enemy ? undefined : ((opts.classDef as ClassLike | null | undefined) ?? BASE_CLASS_OF.get(ability.id));
  const baseCls = BASE_CLASS_OF.get(ability.id);
  const basePassives = new Set((baseCls?.passives ?? []).map((p) => p.id));
  const foldAll = !baseCls; // talent-added ability: every passive that mentions it is part of what it does
  const ownProcs = (ownCls?.passives ?? [])
    .filter((p) => !p.id.startsWith('progression.') && (foldAll || basePassives.has(p.id)))
    .flatMap((p) => (p.procs ?? []).filter((pr) => OWN_PROC_ON.has(pr.on) && pr.abilityIds?.includes(ability.id)));
  desc.push(...procSentences(ownProcs, ctx, { perspective: ability }).map(sentence));
  const descText = desc.filter(Boolean).join(' ');
  if (enemy && ability.description) {
    // Encounter casts: the hand-written summary first (mechanics, affix notes), then the numbers.
    lines.push({ text: ability.description, tone: 'desc' });
    if (descText) lines.push({ text: descText, tone: 'desc' });
  } else if (descText) lines.push({ text: descText, tone: 'desc' });
  else if (ability.description) lines.push({ text: ability.description, tone: 'desc' });

  // ---- talent / gear modifications ----------------------------------------------
  if (!enemy) {
    const base = opts.baseAbility === undefined ? baseAbility(ability.id) : opts.baseAbility;
    const label = opts.modLabel ?? 'Talent';
    const quiet: RenderCtx = { ...ctx, formulas: [] };
    const mods = base ? diffAbility(base, ability, quiet) : [];
    if (base) mods.push(...passiveLines(ability, BASE_CLASS_OF.get(ability.id), opts.classDef ?? undefined, quiet));
    for (const m of [...new Set(mods)]) lines.push({ text: `${label}: ${capitalize(m)}`, tone: 'talent' });
  }

  const tf = opts.transform ?? (enemy ? undefined : autoTransform(ability, content, src));
  if (tf?.from) lines.push({ text: `Replaces ${tf.from.name} while ${viaText(tf.from.via, ability.name)} is active.`, tone: 'good' });
  for (const t of tf?.into ?? []) {
    // Its own effects already say "turns X into Y".
    if (!override && descText.includes(`into ${t.name}`)) continue;
    let own: AuraDef | undefined;
    walkEffects(abilityTrees(ability), (e) => {
      if (e.type === 'applyAura' && e.aura.name === t.via && e.aura.overrides?.length) own ??= e.aura;
    });
    const text = own && own.durationMs > 0 ? `After use, becomes ${t.name} for ${fmtDuration(own.durationMs)}.` : `Becomes ${t.name} while ${viaText(t.via, t.name)} is active.`;
    lines.push({ text, tone: 'white' });
  }

  for (const e of opts.errors ?? []) lines.push({ text: e, tone: 'error' });
  if (opts.keybind) lines.push({ text: `Keybind: ${opts.keybind}`, tone: 'muted' });

  // ---- shift: formulas -----------------------------------------------------------
  const power = caster?.power ?? DEFAULT_POWER;
  extended.push({ text: `Power ${fmtInt(power)}${caster ? '' : ' (default)'} · Haste ${fmtPct(haste)}${caster?.crit !== undefined ? ` · Crit ${fmtPct(caster.crit)}` : ''}${caster?.versatility ? ` · Versatility ${fmtPct(caster.versatility)}` : ''}`, tone: 'formula' });
  for (const f of ctx.formulas) extended.push({ text: f, tone: 'formula' });
  if (ability.cast && ability.cast.type !== 'instant' && haste) {
    const raw = ability.cast.type === 'cast' ? ability.cast.timeMs : ability.cast.durationMs;
    extended.push({ text: `Cast: ${fmtSec(raw)} sec ÷ (1 + ${trimNum(haste, 3)} haste) = ${fmtSec(raw / (1 + haste))} sec`, tone: 'formula' });
  }
  if (!enemy) {
    const gcd = ability.gcdMs ?? BASE_GCD_MS;
    if (gcd > 0) extended.push({ text: `GCD: ${fmtSec(Math.max(MIN_GCD_MS, gcd / (1 + haste)))} sec (base ${fmtSec(gcd)})`, tone: 'formula' });
  }
  if (ability.flatThreat) extended.push({ text: `Flat threat: ${fmtInt(ability.flatThreat)}`, tone: 'formula' });
  if (enemyDef) {
    const t = enemyDef.timer;
    extended.push({ text: `Timer: first ${fmtSec(t.first)}${t.firstMax ? `–${fmtSec(t.firstMax)}` : ''} sec, every ${fmtSec(t.every)}${t.everyMax ? `–${fmtSec(t.everyMax)}` : ''} sec${t.jitter ? ` (±${fmtSec(t.jitter)})` : ''}`, tone: 'formula' });
    if (caster?.damageMult && caster.damageMult !== 1) extended.push({ text: `Keystone damage scaling ×${trimNum(caster.damageMult, 3)}`, tone: 'formula' });
  }
  if (ability.description && descText && !enemy) extended.push({ text: `Note: ${ability.description}`, tone: 'muted' });
  extended.push({ text: `id: ${ability.id}`, tone: 'muted' });

  let icon: string | null = null;
  const resolver = opts.iconUrl === undefined ? defaultIconResolver() : opts.iconUrl;
  if (resolver) {
    try {
      icon = resolver(ability) ?? null;
    } catch {
      icon = null;
    }
  }

  const tags = (ability.ui?.tags ?? []).map((t) => TAG_LABEL[t] ?? t);
  const tag = enemyDef ? KIND_LABEL[enemyDef.kind] ?? 'Enemy ability' : tags.length ? tags.join(' · ') : capitalize(ability.school);

  return {
    kind: 'ability',
    title: ability.name,
    titleTone: enemy ? 'enemy' : 'default',
    tag,
    icon,
    iconColor: ability.ui?.color,
    cost: left1,
    range,
    cast,
    cooldown,
    charges,
    grid,
    lines,
    extended,
  };
}

// ---------------------------------------------------------------------------
// Live casts (cast bars)
// ---------------------------------------------------------------------------

/** Tooltip for whatever `u` is casting right now (null when idle or unknown). */
export function describeUnitCast(
  u: DeepReadonly<Unit> | undefined,
  world: DeepReadonly<World>,
  content: TooltipContent,
  eff?: EffectiveStats | null,
): TooltipModel | null {
  const c = u?.alive ? u.cast : null;
  if (!u || !c) return null;
  const ability = content.ability(c.abilityId);
  if (!ability) return null;
  const enemy = u.faction === 'enemy';
  const m = describeAbility(ability, casterFromUnit(u, eff), content, { enemy, interruptible: c.interruptible, baseAbility: enemy ? null : undefined });
  const left = Math.max(0, c.endsAt - world.time);
  const tgt = c.targetId && c.targetId !== u.id ? world.units[c.targetId]?.name : undefined;
  m.lines.unshift({ text: `${u.name} ${c.kind === 'channel' ? 'channeling' : 'casting'}${tgt ? ` on ${tgt}` : ''} · ${fmtSec(left)} sec left`, tone: 'muted' });
  return m;
}

// ---------------------------------------------------------------------------
// Auras
// ---------------------------------------------------------------------------

export interface AuraTooltipCtx {
  content: TooltipContent;
  now: number;
  /** Name of the unit that applied it. */
  casterName?: string | null;
  /** Caster numbers (for live periodic amounts); the instance snapshot wins when present. */
  caster?: TooltipCaster | null;
  /** Whether the aura's caster is an NPC. */
  enemyCaster?: boolean;
}

export function remainingLabel(ms: number | null): string {
  if (ms === null) return 'Permanent';
  if (ms >= 60_000) return `${Math.ceil(ms / 60_000)} min remaining`;
  return `${ms < 10_000 ? (ms / 1000).toFixed(1) : Math.ceil(ms / 1000)} sec remaining`;
}

type AuraInst = Pick<AuraInstance, 'auraId' | 'stacks' | 'expiresAt' | 'durationMs'> & Partial<Pick<AuraInstance, 'casterId' | 'abilityId' | 'absorbRemaining'>> & { snapshot?: { power: number } | null };

export function describeAura(inst: DeepReadonly<AuraInst>, def: AuraDef | undefined, ctx: AuraTooltipCtx): TooltipModel {
  const name = def?.name ?? prettyId(inst.auraId);
  const rctx: RenderCtx = {
    caster: ctx.caster ?? null,
    content: ctx.content,
    enemy: !!ctx.enemyCaster,
    targeting: 'enemy',
    formulas: [],
    resourceName: (id) => RESOURCE_NAMES.get(id) ?? prettyId(id),
    ...(inst.snapshot?.power ? { powerOverride: inst.snapshot.power } : {}),
  };
  const remaining = inst.expiresAt === null ? null : Math.max(0, inst.expiresAt - ctx.now);
  const lines: TooltipLine[] = [];
  const clauses = def ? auraClauses({ ...def, durationMs: inst.durationMs || def.durationMs }, rctx, { stacks: inst.stacks }) : [];
  if (def) clauses.push(...auraUsers(def, rctx, clauses.length > 0 || !!def.overrides?.length));
  const text = clauses.map(sentence).join(' ');
  if (text) lines.push({ text, tone: 'desc' });
  if (inst.absorbRemaining) lines.push({ text: `Absorbs ${fmtInt(inst.absorbRemaining)} more damage.`, tone: 'desc' });
  // +rotation rework (F2): a transform offer names what the button becomes.
  for (const o of def?.overrides ?? []) {
    const base = ctx.content.ability(o.abilityId)?.name ?? prettyId(o.abilityId);
    const form = ctx.content.ability(o.with)?.name ?? prettyId(o.with);
    lines.push({ text: `${base} becomes ${form}.`, tone: 'good' });
  }
  if (!text && !inst.absorbRemaining && !def?.overrides?.length) lines.push({ text: def?.kind === 'debuff' ? 'Harmful effect.' : 'Helpful effect.', tone: 'desc' });
  const from = inst.abilityId ? ctx.content.ability(inst.abilityId)?.name : undefined;
  if (from || ctx.casterName) lines.push({ text: `${from ? `From ${from}` : 'Applied'}${ctx.casterName ? ` by ${ctx.casterName}` : ''}`, tone: 'muted' });
  const extended: TooltipLine[] = rctx.formulas.map((f) => ({ text: f, tone: 'formula' as const }));
  if (def?.pandemic) extended.push({ text: 'Pandemic: refreshing keeps up to 30% of the remaining duration', tone: 'formula' });
  if (inst.durationMs) extended.push({ text: `Duration ${fmtDuration(inst.durationMs)}`, tone: 'formula' });
  extended.push({ text: `id: ${inst.auraId}`, tone: 'muted' });
  return {
    kind: 'aura',
    title: name,
    titleTone: def?.kind === 'debuff' ? 'debuff' : 'default',
    tag: def?.dispelType ? capitalize(def.dispelType) : def?.kind === 'debuff' ? 'Debuff' : def?.ui?.proc ? 'Proc' : 'Buff',
    icon: null,
    cost: null,
    range: null,
    cast: null,
    cooldown: null,
    charges: null,
    grid: [[remainingLabel(remaining), inst.stacks > 1 ? `${inst.stacks} stacks` : '']],
    lines,
    extended,
  };
}

/**
 * What other abilities do with an aura that has no effect of its own: "Mortal Strike and Execute
 * deal 10% more damage per stack." / "Empowers Mortal Strike."
 */
function auraUsers(def: AuraDef, ctx: RenderCtx, hasText: boolean): string[] {
  const all = ctx.content.abilities;
  if (!all) return [];
  const perStack = new Map<string, string[]>();
  const gated: string[] = [];
  const refs = (c: Condition | undefined): boolean => {
    if (!c) return false;
    if (c.type === 'and') return c.all.some(refs);
    if (c.type === 'or') return c.any.some(refs);
    if (c.type === 'not') return refs(c.cond);
    return (c.type === 'selfHasAura' || c.type === 'targetHasAura') && c.auraId === def.id;
  };
  for (const a of all.values()) {
    if (isEnemyAbility(a) !== !!ctx.enemy) continue;
    let gate = false;
    walkEffects(abilityTrees(a), (e) => {
      if ((e.type === 'damage' || e.type === 'heal') && e.amount.perStack?.auraId === def.id) {
        const k = `${e.type}|${e.amount.perStack.mult}`;
        const list = perStack.get(k) ?? [];
        if (!list.includes(a.name)) list.push(a.name);
        perStack.set(k, list);
      }
      if (refs(e.if) || (e.type === 'conditional' && refs(e.cond))) gate = true;
    });
    if (gate && !gated.includes(a.name)) gated.push(a.name);
  }
  const out: string[] = [];
  // Same abilities and bonus for damage and healing read as one clause.
  const byWho = new Map<string, { kinds: string[]; names: string[]; mult: number }>();
  for (const [k, names] of perStack) {
    const [kind, mult] = k.split('|') as [string, string];
    const key = `${names.join(',')}|${mult}`;
    const g = byWho.get(key) ?? { kinds: [], names, mult: Number(mult) };
    g.kinds.push(kind);
    byWho.set(key, g);
  }
  for (const g of byWho.values()) {
    const many = g.names.length > 1;
    const p = fmtPct(Math.abs(g.mult));
    const more = g.mult > 0 ? 'more' : 'less';
    const v = (one: string, pl: string): string => (many ? pl : one);
    const what =
      g.kinds.length > 1 ? `${v('heals', 'heal')} and ${v('deals', 'deal')} ${p} ${more}` : g.kinds[0] === 'damage' ? `${v('deals', 'deal')} ${p} ${more} damage` : `${v('heals', 'heal')} for ${p} ${more}`;
    out.push(`${joinAnd(g.names)} ${what} per stack`);
  }
  if (!out.length && !hasText && gated.length) out.push(`empowers ${joinAnd(gated)}`);
  return out;
}

/** Plain text tooltip (talent nodes, item effects, anything that is just a name + text). */
export function describeText(title: string, body: string, opts: { tag?: string; extra?: TooltipLine[] } = {}): TooltipModel {
  return {
    kind: 'text',
    title,
    tag: opts.tag,
    cost: null,
    range: null,
    cast: null,
    cooldown: null,
    charges: null,
    grid: [],
    lines: [{ text: body, tone: 'desc' }, ...(opts.extra ?? [])],
    extended: [],
  };
}
